import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Users, Mail, MessageSquare, TrendingUp, Send } from "lucide-react";
import { NextEventBanner } from "./dashboard/NextEventBanner";
import { AliancaCard } from "./dashboard/AliancaCard";
import { EventsBreakdown } from "./dashboard/EventsBreakdown";
import { TopPanels, StatCard } from "./dashboard/TopPanels";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [topHosts, setTopHosts] = useState([]);

  const load = async () => {
    try {
      const [s, h] = await Promise.all([
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/admin/invites/top-hosts?limit=5&year=${new Date().getFullYear()}`, { withCredentials: true }).catch(() => ({ data: { items: [] } })),
      ]);
      setStats(s.data);
      setTopHosts(h.data?.items || []);
    } catch (e) {
      toast.error("Erro a carregar dados.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const sendDigest = async () => {
    setSending(true);
    try {
      const r = await axios.post(`${API}/admin/digest/send-now`, {}, { withCredentials: true });
      toast.success(
        `Resumo enviado — ${r.data.new_sept} inscrições, ${r.data.new_miami} Miami, ${r.data.new_newsletter} newsletter, ${r.data.new_contacts} contactos.`
      );
    } catch (e) {
      toast.error("Erro a enviar resumo.");
    } finally {
      setSending(false);
    }
  };

  const sendTestEmail = async () => {
    const to = window.prompt("Para que email enviar o teste de diagnóstico?", "paulo.galvao@kwportugal.pt");
    if (!to) return;
    try {
      const r = await axios.post(`${API}/admin/diagnostic/send-test-email`, { to_email: to }, { withCredentials: true });
      const ok = !!r.data?.resend_id;
      if (ok) {
        toast.success(`Email aceite por Resend (id: ${r.data.resend_id.slice(0, 8)}…). Verifique a caixa de ${to} em ~30s.`, { duration: 9000 });
      } else {
        toast.error(`Resend devolveu null. RESEND_API_KEY ${r.data?.resend_api_key_configured ? "está" : "NÃO está"} configurada neste ambiente.`, { duration: 12000 });
      }
    } catch (e) {
      toast.error("Erro a enviar email de diagnóstico.");
    }
  };

  return (
    <div className="px-8 py-10 lg:px-12 lg:py-14" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <div className="overline">Resumo</div>
          <h1 className="display-heading text-4xl sm:text-5xl mt-3">Painel CIEES.</h1>
          <span className="accent-rule mt-6 block" />
        </div>
        <div className="flex flex-wrap gap-3">
          <button onClick={sendTestEmail} className="btn-outline" data-testid="admin-test-email-button">
            <Send className="h-4 w-4" /> Testar email
          </button>
          <button
            onClick={sendDigest}
            disabled={sending}
            className="btn-outline disabled:opacity-60"
            data-testid="admin-send-digest-button"
          >
            <Send className="h-4 w-4" /> {sending ? "A enviar…" : "Enviar resumo agora"}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="mt-16 text-[hsl(var(--muted-foreground))]">A carregar…</div>
      ) : !stats ? null : (
        <>
          {stats.next_event && <NextEventBanner nextEvent={stats.next_event} />}

          {/* Top KPIs */}
          <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              icon={Users}
              label="Total Inscrições"
              value={stats.rsvps_total ?? 0}
              hint="em todos os eventos"
              testid="stat-rsvps-total"
            />
            <StatCard
              icon={TrendingUp}
              label="Últimos 7 dias"
              value={stats.rsvps_last_7_days_total ?? 0}
              hint="novas inscrições"
              testid="stat-rsvps-7d"
            />
            <StatCard
              icon={Mail}
              label="Newsletter"
              value={stats.newsletter_subscribers}
              hint="subscritores"
              testid="stat-newsletter"
            />
            <StatCard
              icon={MessageSquare}
              label="Contactos"
              value={stats.contact_messages}
              hint="mensagens"
              testid="stat-contacts"
            />
          </div>

          {stats.alianca_amor && stats.alianca_amor.total > 0 && <AliancaCard alianca={stats.alianca_amor} />}

          <EventsBreakdown events={stats.events_breakdown} />

          <TopPanels topCompanies={stats.top_companies} topHosts={topHosts} />

          <p className="mt-12 text-xs text-[hsl(var(--muted-foreground))]">
            Resumo diário enviado automaticamente todos os dias às 08:00 (Lisboa) para{" "}
            <span className="text-[hsl(var(--foreground))]">geral@ciees.pt</span>.
          </p>
        </>
      )}
    </div>
  );
}
